import { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import { Pagination, Text } from '@hhgtech/hhg-components/components'
import type { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { LOCALE } from '@hhgtech/hhg-components/types'
import innertext from 'innertext'
import dynamic from 'next/dynamic'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { formatRecentPost } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { Loading } from 'components/atoms/loading'
// import { SmartAppBanner } from 'components/atoms/smartAppBanner'
import { AdBlock } from 'components/molecules/adBlock'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/deferFixedBottomAdBlock'
import { CategoryExplainer } from 'components/molecules/categoriesExplainer'
import { CategoryInfo } from 'components/molecules/categoryInfo'
import { ExploreCategoryWrapper } from 'components/molecules/exploreCategoryWrapper'
import { FaqAccordion } from 'components/molecules/faqAccordion'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { ToolCarousel } from 'components/molecules/toolCarousel'
import { ExpertSection } from 'components/organisms/expertSection'
import { MagniteScript } from 'components/templates/appWrappers/MagniteScript'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useMastheadAdEnable } from 'hooks/useMastheadAdEnable'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { MAPPED_CATEGORY_SLUGS, PREGNANCY_SLUG } from 'interfaces/constants'
import {
  Article,
  Category,
  CommunityEmbed,
  // CategoryApi,
  Expert,
  ExpertHeading,
  ExpertUserSummary,
  FooterInfo,
  RecentPost,
  RecentPostApi,
  SiteWideBanner,
  SpotlightSummary,
  WeekOfPregnancy as WeekOfPregnancyType,
} from 'interfaces/types'
import { CategoryKeyword } from 'interfaces/types/Keyword'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'
import { fastdom } from 'utils/fastdom'

import {
  StyledCategoryArticleWrapper,
  StyledCategoryContainer,
  StyledCategoryPage,
  StyledFaqDescription,
  StyledPregnancyWeek,
  StyledSponsored,
} from './index.styled'
import { BaseProps } from '../../../pages/[...slug]'
import { InterstitialAd } from '../article/components/interstitialAd'
import { MastheadAd } from '../article/components/MastheadAd'
import { OutOfPageAd } from '../article/components/OutOfPageAd'

const WeekOfPregnancy = dynamic(() =>
  import('components/molecules/weekOfPregnancy').then(
    (mod) => mod.WeekOfPregnancy,
  ),
)
const EmbeddedCommunity = dynamic(() =>
  import('components/organisms/embededCommunity').then(
    (mod) => mod.EmbeddedCommunity,
  ),
)
const SponsoredBy = dynamic(() =>
  import('components/molecules/sponsoredBy').then((mod) => mod.SponsoredBy),
)
const CategorySubCategory = dynamic(() =>
  import('components/molecules/categoriesSubcategories').then(
    (mod) => mod.CategorySubCategory,
  ),
)
const RiskScreenerCarousel = dynamic(() =>
  import('components/molecules/riskScreenerCarousel').then(
    (mod) => mod.RiskScreenerCarousel,
  ),
)
const MobileBottomNavigationWrapper = dynamic(
  () => import('components/organisms/mobileBottomNavigationWrapper'),
)

export type Props = {
  categoryInfo: Category
  subCategories: Array<Category>
  explainersPosts: Array<Article>
  relatedArticle: RecentPostApi
  categoryParent: Array<Category>
  idExplainerPosts: string
  siteName: string
  currentCategory?: string
  expertsCategory: Array<Expert>
  exploreCategories: Array<RecentPost>
  experts: ExpertUserSummary[]
  community: CommunityEmbed | null
  sitewideBanner?: SiteWideBanner | null
  footerInfo: FooterInfo
  pregnancyWeeks: WeekOfPregnancyType[]
  spotlightsList?: SpotlightSummary[]
  dataNavMenuContent?: NavMenuContentType
  expertHeading: ExpertHeading
  subCategoriesKeywords?: Array<Array<CategoryKeyword>>
  // categoryKeywords?: Array<CategoryKeyword>
} & BaseProps

export const CategoryPageComponent = ({
  categoryInfo,
  subCategories,
  explainersPosts,
  relatedArticle,
  categoryParent,
  idExplainerPosts,
  siteName,
  exploreCategories,
  experts,
  community,
  sitewideBanner,
  footerInfo,
  pregnancyWeeks,
  dataNavMenuContent,
  expertHeading,
  subCategoriesKeywords,
}: // categoryKeywords,
Props) => {
  const { locale, router } = useRouterExtend()

  const { posts, paging } = formatRecentPost(relatedArticle)

  const isMobile = useIsMobile()

  const isPregnancy = decodeURI(categoryInfo.slug) === PREGNANCY_SLUG[locale]

  const [recentPosts, setRecentPosts] = useState<Array<Article>>(posts)
  const [currentPage, setCurrentPage] = useState(paging?.currentPage)
  const [isLoading, setIsLoading] = useState(false)
  const { formatMessage: f } = useIntl()

  const articlesSectionRef = useRef<HTMLDivElement>(null)

  const adUnitPathMobile = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile: true,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, categoryInfo.permalink),
  })
  const adUnitPathDesktop = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile: false,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, categoryInfo.permalink),
  })
  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, categoryInfo.permalink),
  })

  const mastheadAdEnable = useMastheadAdEnable({
    ...categoryInfo?.adsOnArticles,
  })

  const fetchMoreArticles = (page: number) => {
    const categoryId = categoryInfo.termId
    setIsLoading(true)
    const oldPage = currentPage
    setCurrentPage(page)

    callApiNew(PATHS.ARTICLE.GET_ARTICLE_OF_CATEGORY_NO_EXPLAINERS, {
      params: {
        categoryId: categoryId,
        currentPage: page,
        ids: idExplainerPosts,
      },
    })
      .then((res) => {
        try {
          const { posts, paging } = formatRecentPost(res)
          setRecentPosts(posts)
          setCurrentPage(paging?.currentPage)

          router.push(
            `${categoryInfo.permalink}?page=${paging?.currentPage}`,
            undefined,
            {
              shallow: true,
              scroll: false,
            },
          )
          fastdom.measure(() => {
            const articleBound = articlesSectionRef?.current?.getBoundingClientRect()
            if (articleBound) {
              const scrollTop =
                typeof window !== 'undefined'
                  ? window.pageYOffset || document.documentElement.scrollTop
                  : 0
              window.scrollTo(0, scrollTop + articleBound.top - 80)
            }
          })
          setIsLoading(false)
        } catch (err: any) {
          console.log(err)
          setIsLoading(false)
          setCurrentPage(oldPage)
        }
      })
      .catch((err) => {
        setIsLoading(false)
        setCurrentPage(oldPage)
        console.log(err)
      })
  }

  const handleClick = (e: Event & { nativeEvent: PointerEvent }) => {
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
  }

  useEffect(() => {
    setRecentPosts(posts)
    setCurrentPage(paging?.currentPage)
  }, [relatedArticle])

  const contentId = useMemo(() => {
    if (typeof window !== 'undefined') {
      const contentIdContainer = document.querySelector<HTMLMetaElement>(
        'meta[name="content_id"]',
      )
      return contentIdContainer?.content
    }
    return undefined
  }, [categoryInfo])

  const faqs = useMemo(() => {
    return categoryInfo?.faqs?.map((faq) => ({
      title: (
        <Text size={isMobile ? 's2' : 's3'} type="semiBold">
          {faq.title}
        </Text>
      ),
      description: (
        <StyledFaqDescription>
          <Text
            size="p3"
            as="span"
            dangerouslySetInnerHTML={{ __html: faq.content }}
          ></Text>
          {faq.learnMore && (
            <Text size="p3">
              {f({
                id: 'categoryPage.faqs.learnMore',
                defaultMessage: 'Learn More',
              })}
              : <a href={faq.learnMore.permalink}>{faq.learnMore.title}</a>
            </Text>
          )}
        </StyledFaqDescription>
      ),
    }))
  }, [categoryInfo?.faqs])

  const showInterstitialAd = useMemo(() => {
    const categorySlug = categoryInfo?.permalink
    if (!categorySlug) return false
    const mappedCategorySlugs = MAPPED_CATEGORY_SLUGS[locale]
    if (
      locale === LOCALE.Indonesia ||
      locale === LOCALE.Taiwan ||
      // locale === LOCALE.Vietnam ||
      locale === LOCALE.Malaysia ||
      locale === LOCALE.Thailand
    ) {
      return !(
        (
          categorySlug.includes(`/${mappedCategorySlugs.parenting}/`) ||
          categorySlug.includes(`/${mappedCategorySlugs.pregnancy}/`)
        )
        // categorySlug.includes(`/${mappedCategorySlugs.womensHealth}/`)
      )
    } else if (
      locale === LOCALE.India ||
      locale === LOCALE.PhilippinesEnglish ||
      locale === LOCALE.PhilippinesTagalog
    ) {
      return true
    }
    return false
  }, [categoryInfo?.permalink, locale])

  const showManiteScript = useMemo(() => {
    if (
      categoryInfo?.permalink === '/health/' ||
      categoryInfo?.catParent?.permalink === '/health/'
    )
      return true
    return false
  }, [categoryInfo])

  return (
    <>
      {/* {locale === LOCALE.Vietnam && isMobile && (
        <SmartAppBanner location="Top" />
      )} */}
      {/* HHD-5712 */}
      {showManiteScript && <MagniteScript />}
      <NavigatorLayout
        SEOData={{
          totalPages: paging?.maxNumPages,
          currentPage: currentPage,
          title: `${categoryInfo.categoryName} - ${siteName} - ${f(
            {
              id: 'category.title',
              defaultMessage: 'Page {number} of {total}',
            },
            {
              number: currentPage,
              total: paging?.maxNumPages,
            },
          )}`,
          image: categoryInfo.featureImage || categoryInfo.iconUrl,
          apiMetaData: categoryInfo.metaTag,
          isIndexable: true,
          description: categoryInfo.description,
          ...(locale === LOCALE.Vietnam && isPregnancy
            ? {
                customMetaData: [
                  {
                    name: 'apple-itunes-app',
                    content: `app-id=1645060016, app-argument=https://${
                      domainLocales[locale || LOCALE.Vietnam]
                    }${router.asPath}`,
                  },
                ],
              }
            : {}),
        }}
        isSponsored={!!categoryInfo.isSponsored}
        sitewideBanner={sitewideBanner}
        footerInfo={footerInfo}
        languageOptions={categoryInfo?.translateOptions || []}
        dataNavMenuContent={dataNavMenuContent}
      >
        <AdBlockContainer>
          {showInterstitialAd && (
            <InterstitialAd
              customTargetings={{
                Content_ID: contentId || '',
                Brand: [innertext(categoryInfo?.sponsor?.title || '')],
              }}
              adUnitPath={adUnitPath}
            />
          )}
          <MastheadAd
            adUnitPath={adUnitPath}
            initialSticky
            className={'category-masthead'}
            hasPlaceholder={mastheadAdEnable}
          />
        </AdBlockContainer>
        <StyledCategoryPage data-type-mobile={isMobile}>
          <StyledCategoryContainer data-type-mobile={isMobile}>
            <HealthToolBreadcrumb
              pageItem={[
                ...categoryParent.map((c) => ({
                  label: c.displayName || '',
                  value: c.permalink || '',
                })),
              ]}
            />
            <CategoryInfo
              isMobile={isMobile}
              imgBannerSrc={categoryInfo.featureImage || categoryInfo.iconUrl}
              categoryName={
                categoryInfo.categoryName &&
                innertext(categoryInfo.categoryName)
              }
              description={
                categoryInfo.description && innertext(categoryInfo.description)
              }
              colorFeatureImage={categoryInfo.imageColor}
              categoryTextColor={categoryInfo.textColor}
              reviewedBy={categoryInfo.reviewedBy}
              reviewedDate={categoryInfo.reviewedDate}
            />
            {categoryInfo.sponsor && categoryInfo.sponsor.logo ? (
              <StyledSponsored data-type-mobile={isMobile}>
                <SponsoredBy
                  isMobile={isMobile}
                  image={categoryInfo.sponsor.logo}
                  href={categoryInfo.sponsor.link}
                />
              </StyledSponsored>
            ) : null}
            {categoryInfo.suggestionTools &&
              categoryInfo.suggestionTools.length > 0 && (
                <ToolCarousel
                  tools={categoryInfo.suggestionTools || []}
                  style={{ marginBlock: '48px' }}
                />
              )}
            <CategoryExplainer
              isMobile={isMobile}
              explainerPosts={explainersPosts}
              categoryName={innertext(categoryInfo.categoryName)}
              categoryTextColor={categoryInfo.textColor}
              currentCategory={categoryInfo}
            />
            {pregnancyWeeks.length > 0 && (
              <StyledPregnancyWeek>
                <WeekOfPregnancy
                  className="homepage-weekofpregnancy"
                  data={pregnancyWeeks}
                />
              </StyledPregnancyWeek>
            )}

            <div className="banner-ad mobile-only">
              <AdBlockContainer>
                {isMobile && (
                  <AdBlock
                    adUnitPath={adUnitPathMobile}
                    size={[
                      [300, 250],
                      [300, 600],
                    ]}
                    divId={'div-gpt-ad-topbanner-mobile-' + categoryInfo.termId}
                    pos={['TopBanner']}
                    contentId={contentId}
                  />
                )}
              </AdBlockContainer>
            </div>
            {subCategories && subCategories.length > 0 && (
              <>
                <CategorySubCategory
                  isMobile={isMobile}
                  subCategories={subCategories}
                  categoryName={innertext(categoryInfo.categoryName)}
                />
              </>
            )}

            {categoryInfo.suggestionSubots &&
              categoryInfo.suggestionSubots.subots.length > 0 && (
                <RiskScreenerCarousel
                  riskScreener={categoryInfo.suggestionSubots}
                />
              )}
            {isMobile && faqs && faqs.length > 0 && (
              <FaqAccordion
                template="category"
                faqs={faqs}
                title={f({
                  id: 'faq.title',
                  defaultMessage: 'Frequently Asked Questions',
                })}
              />
            )}
            <div className="banner-ad mobile-only">
              {isMobile && (
                <AdBlockContainer>
                  <AdBlock
                    adUnitPath={adUnitPathMobile}
                    size={[
                      [300, 250],
                      [300, 600],
                    ]}
                    divId={
                      'div-gpt-ad-midbanner1-mobile-' + categoryInfo.termId
                    }
                    pos={['MidBanner1']}
                    contentId={contentId}
                  />
                </AdBlockContainer>
              )}
            </div>
            <StyledCategoryArticleWrapper
              data-type-mobile={isMobile}
              className="mt-8"
            >
              <div
                ref={articlesSectionRef}
                className="articles"
                style={{ position: 'relative' }}
              >
                {isPregnancy ? (
                  exploreCategories.map((explore, i) => {
                    return (
                      <ExploreCategoryWrapper
                        key={i}
                        posts={explore.posts}
                        category={subCategories[i]}
                        keywords={subCategoriesKeywords?.[i] || []}
                      />
                    )
                  })
                ) : (
                  <Fragment>
                    <ExploreCategoryWrapper
                      posts={recentPosts}
                      category={categoryInfo}
                      // keywords={categoryKeywords}
                    />

                    {isLoading && <Loading />}

                    {paging?.maxNumPages > 1 && (
                      <div
                        className="pagination"
                        style={
                          isMobile
                            ? { width: '100%' }
                            : !isMobile && paging?.maxNumPages > 8
                            ? { width: '50%' }
                            : { width: '35%' }
                        }
                      >
                        <Pagination
                          pagesAmount={paging?.maxNumPages}
                          currentPage={currentPage}
                          onChange={(p) => {
                            if (!isLoading) {
                              fetchMoreArticles(p)
                            }
                          }}
                          href="?page="
                          onClick={(e) => handleClick(e as any)}
                        />
                      </div>
                    )}
                  </Fragment>
                )}
              </div>
              <div className="right-section">
                {!isMobile && (
                  <AdBlockContainer>
                    <div className="top-ad">
                      <AdBlock
                        adUnitPath={adUnitPathDesktop}
                        size={[
                          [300, 250],
                          [300, 600],
                        ]}
                        divId={
                          'div-gpt-ad-topbanner-desktop-' + categoryInfo.termId
                        }
                        pos={['TopBanner']}
                        contentId={contentId}
                      />
                    </div>
                  </AdBlockContainer>
                )}
                {!isMobile && faqs && faqs.length > 0 && (
                  <FaqAccordion
                    template="category"
                    faqs={faqs}
                    title={f({
                      id: 'faq.title',
                      defaultMessage: 'Frequently Asked Questions',
                    })}
                  />
                )}
                {!isMobile && (
                  <div className="top-ad">
                    <AdBlockContainer>
                      <AdBlock
                        adUnitPath={adUnitPathDesktop}
                        size={[
                          [300, 250],
                          [300, 600],
                        ]}
                        divId={
                          'div-gpt-ad-midbanner1-desktop-' + categoryInfo.termId
                        }
                        pos={['MidBanner1']}
                        contentId={contentId}
                      />
                    </AdBlockContainer>
                  </div>
                )}
                {!isMobile && community ? (
                  <div className="community-container">
                    <EmbeddedCommunity
                      isMobile={isMobile}
                      columns={1}
                      communityId={community?.id}
                      titleMember={community?.memberTitle}
                      titleGuest={community?.guestTitle}
                      descriptionMember={community?.memberDesc}
                      descriptionGuest={community?.guestDesc}
                    />
                  </div>
                ) : null}
                {!isMobile && (
                  <AdBlockContainer>
                    <div className="bottom-ad">
                      <AdBlock
                        adUnitPath={adUnitPathDesktop}
                        size={[
                          [300, 250],
                          [300, 600],
                        ]}
                        divId={
                          'div-gpt-ad-midbanner2-desktop-' + categoryInfo.termId
                        }
                        pos={['MidBanner2']}
                        contentId={contentId}
                      />
                    </div>
                  </AdBlockContainer>
                )}
              </div>
            </StyledCategoryArticleWrapper>
            {/* {isMobile && (
              <AdBlock
                adUnitPath={adUnitPathMobile}
                size={[
                  [300, 250],
                  [300, 600],
                ]}
                divId={'div-gpt-ad-midbanner2-mobile-' + categoryInfo.termId}
                pos={['MidBanner2']}
                contentId={contentId}
              />
            )} */}
            {/* community */}
            {isMobile && community ? (
              <EmbeddedCommunity
                isMobile={isMobile}
                communityId={community?.id}
                titleMember={community?.memberTitle}
                titleGuest={community?.guestTitle}
                descriptionMember={community?.memberDesc}
                descriptionGuest={community?.guestDesc}
              />
            ) : null}
            {/* {isMobile && (
              <AdBlock
                adUnitPath={adUnitPathMobile}
                size={[
                  [300, 250],
                  [300, 600],
                ]}
                divId={'div-gpt-ad-endbanner-mobile-' + categoryInfo.termId}
                pos={['EndBanner']}
                contentId={contentId}
                style={{
                  marginBottom: '1rem',
                }}
              />
            )} */}

            <ExpertSection
              expertHeading={expertHeading}
              expertsPosts={experts}
              style={{
                padding: 0,
              }}
            />
          </StyledCategoryContainer>
          {/* HHD-5870: Remove ad refresh from Sticky pos */}
          <AdBlockContainer>
            <FixedBottomAdBlock
              divId={'div-gpt-ad-category-sticky-' + categoryInfo.termId}
              className="article-bottom-ad"
              adUnitPath={adUnitPath}
              isMobile={isMobile}
              ignoreRefreshAd={!!(locale === LOCALE.Taiwan && showManiteScript)}
            />
          </AdBlockContainer>
          <OutOfPageAd
            id={`div-gpt-out-of-page-${categoryInfo.termId}`}
            key={String(categoryInfo.termId)}
            className="article-gpt-out-of-page-ad"
            adUnitPath={adUnitPath}
          />
          <MobileBottomNavigationWrapper />
        </StyledCategoryPage>
      </NavigatorLayout>
    </>
  )
}
